import React, { useMemo } from 'react';
import { CareProgramTopicCard } from 'components/CareProgramTopicCard/CareProgramTopicCard';
import { useDeviceType } from 'contexts/ThemeContext/hooks/useDeviceType';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { useNavigate } from 'features/cobrand/hooks/useCobrandUtils';
import { useResources } from 'features/resources/hooks';
import { getHealthTopicTranslationKey } from 'features/resources/utils/healthTopicUtils';
import { PendoEventType } from 'features/pendo/types';
import { usePendo } from 'features/pendo/hooks/usePendo';
import { ResourceCardGrid, ResourceCardGridCell } from './ResourceCardGrid';
import { ResourceSection } from './ResourceSection';

export const ResourceCareProgramTopicSection = () => {
  const { isMobile, isTablet } = useDeviceType();
  const { translation } = useTranslationContext();
  const navigate = useNavigate();
  const { healthTopics } = useResources();
  const { trackEvent } = usePendo();

  const sectionTitleKey = isMobile || isTablet ? 'care_program_topics' : 'program_topics';

  const displayTopics = useMemo(() => {
    if (isMobile || isTablet) return healthTopics.slice(0, 4);
    return healthTopics.slice(0, 5);
  }, [isMobile, isTablet, healthTopics]);

  if (displayTopics.length === 0) {
    return null;
  }

  const handleViewAll = () => {
    trackEvent(PendoEventType.ALL_CARE_PROGRAMS, {
      eventType: PendoEventType.ALL_CARE_PROGRAMS,
    });

    navigate('/resources/topics');
  };

  const handleTopicClick = (topicId: string) => {
    trackEvent(PendoEventType.SPECIFIC_HEALTH_TOPIC, {
      eventType: PendoEventType.SPECIFIC_HEALTH_TOPIC,
      topicId,
    });

    navigate(`/resources/topics?topicId=${topicId}`);
  };

  return (
    <ResourceSection
      description={translation('care_program_section_body')}
      title={translation(sectionTitleKey)}
      titleId="program_topics"
      viewAllText={translation('care_programs_cta')}
      viewAllAriaLabel={`${translation('care_programs_cta')} - ${translation(sectionTitleKey)}`}
      onViewAll={handleViewAll}
    >
      <ResourceCardGrid>
        {displayTopics.map((topic) => {
          const translatedTitle = translation(getHealthTopicTranslationKey(topic.healthTopic));

          return (
            <ResourceCardGridCell key={topic.identifier}>
              <CareProgramTopicCard
                healthTopic={topic.healthTopic}
                title={translatedTitle}
                ariaLabel={`${translatedTitle} care program`}
                onClick={() => handleTopicClick(topic.healthTopic)}
              />
            </ResourceCardGridCell>
          );
        })}
      </ResourceCardGrid>
    </ResourceSection>
  );
};
